import { createI18n, I18nOptions } from 'vue-i18n';
import { resolveValue, parse, Path, PathValue } from '@intlify/core-base';
import { isObject, isFunction } from '@intlify/shared';

import de from '@/locales/de.json';
import en from '@/locales/en.json';
import { Key } from './types/constants/Key';
import { computed, ComputedRef, Ref } from 'vue';

const messages = { de: de, en: en };

const language = navigator.language.substring(0, 2);
let locale = language && Object.keys(messages).includes(language) ? language : import.meta.env.VITE_I18N_LOCALE || Key.language.default;

const storedLang = localStorage.getItem(Key.storage.locale);
if (storedLang) {
  locale = storedLang;
} else {
  locale = Key .language.default;
}

const i18n = createI18n({
  legacy: true,
  locale: locale,
  fallbackLocale: import.meta.env.VITE_I18N_FALLBACK_LOCALE || Key .language.default,
  globalInjection: true,
  messages: messages,
  missingWarn: true,
  missing: (locale, key) => {
    return '@' + locale + ':' + key;
  },
  messageResolver: (obj, path): PathValue => {
    let value = resolveValue(obj, path);
    if (value) return value;

    //try resolving the value case insensitive
    value = resolveValueCaseInsensitive(obj, path);
    if (value) return value;

    return null;
  },
} as I18nOptions);

export function getI18nLocale(): string {
  // Hack / Needs investigation / FIXME:
  // When compiled, this property ('locale') is a ComputedRef & the usual
  // JS mechanism of reading/setting values through a proxy object doesn't work properly.
  return (i18n?.global?.locale as unknown as { value:string }).value ?? i18n?.global?.locale ?? Key.language.default;
}

export function setI18nLocale(lang: string) {
  i18n.global.locale = lang;
  localStorage.setItem(Key.storage.locale, lang);
}

const caseInsensitiveCache = new Map<Path, string[]>();

// based on "resolveValue" from @intlify/core-base
export function resolveValueCaseInsensitive(obj: any, path: Path): PathValue {
  if (!isObject(obj)) return null;

  const pathLowerCase = path.toLocaleLowerCase();
  let hit = caseInsensitiveCache.get(pathLowerCase) || parseAndCachePath(pathLowerCase);
  if (!hit) return null;

  let current: any = obj;
  for (const segment of hit) {
    if (isFunction(current)) return null;

    current = getValueCaseInsensitive(current, segment, path);
    if (current === undefined) return null;
  }

  return current;
}

function parseAndCachePath(path: string): string[] | null {
  const parsed = parse(path);
  if (parsed) {
    caseInsensitiveCache.set(path, parsed);
    return parsed;
  }
  return null;
}

function getValueCaseInsensitive(obj: any, keyLower: string, originalPath: string): any {
  if (!obj || typeof obj !== 'object') return undefined;

  if (keyLower in obj) return obj[keyLower];

  for (const k in obj) {
    if (k.toLowerCase() === keyLower) {
      console.warn(`i18n key '${originalPath}' was resolved as case-insensitive`);
      return obj[k];
    }
  }

  return undefined;
}
export default i18n;
